import { decamelizeKeys } from 'humps'
import { httpServiceClient } from '@/infra/http'
import { makeApiUrl } from '@/main/factories/http/api-url-factory'
import { ServerError } from '@/domain/errors'
import { parseConfiguration } from '@/data/services/configuration/configuration-service-utils'
import {
  Configuration,
  RawConfiguration
} from '@/data/services/configuration/configuration-service-types'
import {
  CompanyInfoDetail,
  CompanyCreateRequest,
  CompanyCreateResponse,
  CompanyGeneral,
  CompanyListParams,
  CompanyListResponse,
  CompanyUpdateInfoDetailRequest,
  PassPlan
} from './company-service-types'

export const createCompanyService = (client = httpServiceClient) => ({
  /**
   * Fetch active pass plans (used to build license count fields).
   *
   * ENDPOINT: GET /api/v2/plans
   * TODO (migração v2): Confirm response contract with backend team.
   */
  async fetchPassPlans(): Promise<PassPlan[]> {
    const response = await client<PassPlan[]>({
      method: 'get',
      url: makeApiUrl('/v2/plans')
    })

    return response.body.data as PassPlan[]
  },

  /**
   * Fetch default configuration definitions for a new company.
   *
   * V2 ENDPOINT: GET /api/company/v2/companies/configurations/defaults
   * Returns the configuration schema with default/effective values pre-filled.
   */
  async fetchConfigurationDefaults(): Promise<Configuration.Model[]> {
    const response = await client<RawConfiguration[]>({
      method: 'get',
      url: makeApiUrl('/company/v2/companies/configurations/defaults')
    })

    return (response.body.data ?? []).map(item => parseConfiguration(item as RawConfiguration))
  },

  /**
   * Create a new company.
   *
   * V2 ENDPOINT: POST /api/company/v2/companies
   * Content-Type: multipart/form-data
   *   - part `command`: JSON payload (application/json)
   *   - part `logo`:    image file (optional)
   */
  async createCompany(data: CompanyCreateRequest): Promise<CompanyCreateResponse> {
    const { logo, ...payload } = data

    const formData = new FormData()
    formData.append('command', new Blob([JSON.stringify(payload)], { type: 'application/json' }))

    if (logo) {
      formData.append('logo', logo)
    }

    const response = await client<CompanyCreateResponse>({
      method: 'post',
      url: makeApiUrl('/company/v2/companies'),
      body: formData
    })

    // Defesa contra resposta inválida que não foi pega pelo handleError do http client:
    // se chegou até aqui sem uniqueId, força o throw pra evitar feedback de sucesso falso.
    // Usa ServerError (erro de domínio) em vez de string hardcoded — a mensagem
    // exibida ao usuário vem do próprio erro de domínio do projeto.
    const result = response.body.data as CompanyCreateResponse

    if (!result?.uniqueId) {
      throw new ServerError()
    }

    return result
  },

  /**
   * Busca os dados básicos da empresa (tab "Dados da empresa").
   *
   * V2 ENDPOINT: GET /api/company/v2/companies/:id/basic-data
   */
  async fetchCompanyInfoDetail(id: string): Promise<CompanyInfoDetail> {
    const response = await client<CompanyInfoDetail>({
      method: 'get',
      url: makeApiUrl(`/company/v2/companies/${id}/basic-data`)
    })

    return response.body.data as CompanyInfoDetail
  },

  /**
   * Salva os dados básicos da empresa.
   *
   * V2 ENDPOINT: PUT /api/company/v2/companies/:id/basic-data
   * Content-Type: multipart/form-data
   *   - part `command`: JSON payload (application/json)
   *   - part `logo`:    image file (opcional, só quando trocou)
   *
   * Resposta: DTO atualizado (mesmo shape do GET) para o front sincronizar
   * o estado sem precisar refetch.
   */
  async updateCompanyInfoDetail(
    id: string,
    data: CompanyUpdateInfoDetailRequest
  ): Promise<CompanyInfoDetail> {
    const { logo, ...payload } = data

    const formData = new FormData()
    // O body é FormData, então o interceptor do http client NÃO decameliza (ele pula
    // FormData). Decamelizamos o `command` na mão pro backend receber snake_case
    // (`short_name`, `politics_url`) — senão chaves multi-palavra chegam camelCase e
    // são ignoradas (salvas como null).
    formData.append(
      'command',
      new Blob([JSON.stringify(decamelizeKeys(payload))], { type: 'application/json' })
    )

    if (logo) {
      formData.append('logo', logo)
    }

    const response = await client<CompanyInfoDetail>({
      method: 'put',
      url: makeApiUrl(`/company/v2/companies/${id}/basic-data`),
      body: formData
    })

    return response.body.data as CompanyInfoDetail
  },

  /**
   * Busca os dados gerais da empresa (nome + ativa/bloqueada + dados do bloqueio)
   * para o header e os botões de status. Carregado no loader da página, independente
   * da tab ativa.
   *
   * V2 ENDPOINT: GET /api/company/v2/companies/:id/general
   * Ponto único de dados compartilhados entre tabs — substitui o GET legado gordo
   * e o GET /block isolado.
   */
  async fetchCompanyGeneral(id: string): Promise<CompanyGeneral> {
    const response = await client<CompanyGeneral>({
      method: 'get',
      url: makeApiUrl(`/company/v2/companies/${id}/general`)
    })

    return response.body.data as CompanyGeneral
  },

  /** Bloqueia novas compras da empresa. POST /api/company/v2/companies/:id/block */
  async blockCompany(id: string): Promise<void> {
    await client({ method: 'post', url: makeApiUrl(`/company/v2/companies/${id}/block`) })
  },

  /** Desbloqueia novas compras da empresa. DELETE /api/company/v2/companies/:id/block */
  async unblockCompany(id: string): Promise<void> {
    await client({ method: 'delete', url: makeApiUrl(`/company/v2/companies/${id}/block`) })
  },

  /** Desativa (churn) a empresa — permanente. POST /api/company/v2/companies/:id/churn */
  async churnCompany(id: string): Promise<void> {
    await client({ method: 'post', url: makeApiUrl(`/company/v2/companies/${id}/churn`) })
  },

  /** Reativa a empresa. POST /api/company/v2/companies/:id/activation */
  async activateCompany(id: string): Promise<void> {
    await client({ method: 'post', url: makeApiUrl(`/company/v2/companies/${id}/activation`) })
  },

  /**
   * Busca lista paginada de empresas com filtro por nome.
   *
   * LEGACY ENDPOINT: GET /api/company/list
   * TODO (migração v2): Trocar para GET /backoffice/v2/companies e adaptar o mapeamento
   * da resposta de { pages, results } para { data: { items, page, pageSize, total, totalPages } }.
   */
  async fetchCompanyList(
    params: CompanyListParams,
    signal?: AbortSignal
  ): Promise<CompanyListResponse> {
    const response = await client<CompanyListResponse>({
      method: 'get',
      url: makeApiUrl('/company/list'),
      params: {
        page: params.page,
        companyName: params.companyName
      },
      signal
    })

    return response.body.data as CompanyListResponse
  }
})

export const companyService = createCompanyService()
